import { gql } from 'graphql-request'
import { LONG_STALE_TIME, reportGqlError, request, useQueryOriginal } from '../../api'
import {
  APP_SUBSCRIPTION_PRODUCTS_QUERY_KEY,
  APP_SUBSCRIPTION_QUERY_KEY,
  CURRENT_BUSINESS_MAX_EMPLOYEES_QUERY_KEY,
  CURRENT_BUSINESS_SUBSCRIPTION_QUERY_KEY,
  PLAN_TYPES_QUERY_KEY,
} from './queryKeys'
import { CurrentBusinessSubscription, GetPaddleProductsFunctionResult } from './logic'
import { ServerAppSubscriptionType, ServerPlanType } from '../../generated/graphql-types'
import { appSubscriptionFragment } from './appSubscriptions.fragments'
import { DEFAULT_TIMEZONE, toZonedTime } from '@expane/date'

export function useFetchLastBusinessCurrentPlanMaxEmployees(enabled = true) {
  return useQueryOriginal<number | null>(
    [APP_SUBSCRIPTION_QUERY_KEY, CURRENT_BUSINESS_MAX_EMPLOYEES_QUERY_KEY],
    async () => {
      const result = await request(gql`
        query {
          businessCurrentPlanTypeView {
            plan {
              maxEmployees
            }
          }
        }
      `)

      if (result.businessCurrentPlanTypeView?.[0])
        return result.businessCurrentPlanTypeView[0].plan.maxEmployees

      return null
    },
    { onError: reportGqlError, enabled, staleTime: LONG_STALE_TIME },
  )
}

export function useFetchBusinessCurrentSubscription(timezone: string | undefined, enabled = true) {
  return useQueryOriginal<CurrentBusinessSubscription | null>(
    [APP_SUBSCRIPTION_QUERY_KEY, CURRENT_BUSINESS_SUBSCRIPTION_QUERY_KEY],
    async () => {
      const result = await request(gql`
        query {
          businessCurrentPlanTypeView {
            status
            createdAt
            pausedAt
            canceledAt
            planType
          }
        }
      `)

      if (
        Array.isArray(result.businessCurrentPlanTypeView) &&
        result.businessCurrentPlanTypeView[0]
      )
        return {
          ...result.businessCurrentPlanTypeView[0],
          canceledAt: result.businessCurrentPlanTypeView[0].canceledAt
            ? toZonedTime(
                result.businessCurrentPlanTypeView[0].canceledAt,
                timezone || DEFAULT_TIMEZONE,
              )
            : null,
        }

      return null
    },
    { onError: reportGqlError, enabled, staleTime: LONG_STALE_TIME },
  )
}

export function useFetchAppSubscriptionProducts() {
  return useQueryOriginal<GetPaddleProductsFunctionResult | undefined>(
    [APP_SUBSCRIPTION_QUERY_KEY, APP_SUBSCRIPTION_PRODUCTS_QUERY_KEY],
    async () => {
      const result = await request(gql`
        query {
          getPaddleProducts {
            code
            message
            products
          }
        }
      `)

      if (result?.getPaddleProducts) return result.getPaddleProducts
    },
    { onError: reportGqlError, staleTime: LONG_STALE_TIME },
  )
}

export function useFetchPlanTypes() {
  return useQueryOriginal<ServerPlanType[] | undefined>(
    [APP_SUBSCRIPTION_QUERY_KEY, PLAN_TYPES_QUERY_KEY],
    async () => {
      const result = await request(gql`
        query {
          planTypes {
            type
            maxEmployees
          }
        }
      `)

      if (Array.isArray(result?.planTypes)) return result.planTypes
    },
    { onError: reportGqlError, staleTime: LONG_STALE_TIME },
  )
}

export function useFetchAppSubscriptions(timezone: string | undefined) {
  return useQueryOriginal<ServerAppSubscriptionType[] | undefined>(
    [APP_SUBSCRIPTION_QUERY_KEY],
    async () => {
      const result = await request<{ appSubscriptions?: ServerAppSubscriptionType[] | null }>(gql`
        ${appSubscriptionFragment}
        query {
          appSubscriptions(order_by: { createdAt: desc }) {
            ...appSubscriptionType
          }
        }
      `)

      if (Array.isArray(result?.appSubscriptions))
        return result.appSubscriptions.map(appSubscription => ({
          ...appSubscription,
          createdAt: toZonedTime(appSubscription.createdAt, timezone || DEFAULT_TIMEZONE),
          startedAt: appSubscription.startedAt
            ? toZonedTime(appSubscription.startedAt, timezone || DEFAULT_TIMEZONE)
            : null,
          canceledAt: appSubscription.canceledAt
            ? toZonedTime(appSubscription.canceledAt, timezone || DEFAULT_TIMEZONE)
            : null,
          pausedAt: appSubscription.pausedAt
            ? toZonedTime(appSubscription.pausedAt, timezone || DEFAULT_TIMEZONE)
            : null,
          nextBilledAt: appSubscription.nextBilledAt
            ? toZonedTime(appSubscription.nextBilledAt, timezone || DEFAULT_TIMEZONE)
            : null,
          firstBilledAt: appSubscription.firstBilledAt
            ? toZonedTime(appSubscription.firstBilledAt, timezone || DEFAULT_TIMEZONE)
            : null,
        }))
    },
    { onError: reportGqlError, enabled: Boolean(timezone) },
  )
}
